import {
  FcCenterText,
  FcColorType,
  FcContainer,
  FcGrid,
  FcLogo,
  FcOverlay,
  FcPositionElement,
  FcPositionProperty,
  FcReservedArea,
  FcShape,
  FcSize,
  FcSvg,
  fcBuildSvgColor,
  getDistance,
  getIntersection,
  getRimIndent
} from '@dtx-company/flowcode-generator-types/src'
import { fcGenerateGradient } from './fc_gradient'
import { fcGetDomId, fcResetDomId } from './fc_dom'
import { fcGetOverlayMargin, fcRenderDataGrid } from './fc_datagrid_renderer'
import { fcPositionElementRenderer } from './fc_position_element_renderer'
import {
  fcRenderContainer,
  fcRenderContainerInsetBackground,
  fcRenderContainerMaskImage,
  fcRenderContainerOverlay,
  fcRenderContainerRim,
  fcRenderContainerText
} from './fc_container_renderer'
import { roundDecimal } from './fc_utils'

export async function fcRenderFlowcode(
  container: FcContainer,
  positionElements: FcPositionElement[],
  dataGrid: FcGrid,
  targetSize: FcSize,
  logo?: FcLogo,
  margin?: number,
  centerText?: FcCenterText,
  overlays?: FcOverlay[]
): Promise<FcSvg> {
  let parentSvg: FcSvg | undefined = undefined
  let sizePadding = 0
  let inset = false
  let minMargin = 0
  fcResetDomId()
  if (overlays) {
    minMargin = await fcGetOverlayMargin(dataGrid, overlays, targetSize, targetSize)
    if (!margin || margin < minMargin) {
      margin = minMargin
      if (container.overlayExpandTargetSize) {
        targetSize += minMargin
      }
    }
  }
  if (margin && !container.borderInsetEyes) {
    parentSvg = new FcSvg(0, 0, targetSize, targetSize, container.useCmyk)
    targetSize -= margin * 2
    if (margin === container.borderSize) {
      // for compatibility with old style rendering which included margin in size calculations for datagrid
      sizePadding = container.borderSize
    }
  } else if (container.borderInsetEyes && container.backgroundInsetExtend) {
    parentSvg = new FcSvg(0, 0, targetSize, targetSize, container.useCmyk)
    margin = (container.backgroundInsetExtend / 100) * targetSize
    if (margin < minMargin) {
      margin = minMargin
    }
    targetSize -= margin * 2
    inset = true
    await fcRenderContainerInsetBackground(container, parentSvg)
  } else {
    margin = minMargin
  }

  const reserved = new FcReservedArea()
  if (container.rimWidth) {
    // convert from percentage
    container.rimWidth = (container.rimWidth * targetSize) / 100 / 2
    container.rimPatternSize = ((container.rimPatternSize || 0) * container.rimWidth) / 100
    container.rimPatternSpacing = ((container.rimPatternSpacing || 0) * container.rimWidth) / 100
  }
  const rimIndent = getRimIndent(container)
  const targetSizeInner = targetSize - rimIndent * 2
  const indent = rimIndent || margin
  const containerSvg = new FcSvg(
    indent,
    indent,
    targetSizeInner,
    targetSizeInner,
    container.useCmyk
  )

  if (container.borderInsetEyes !== undefined) {
    const area = dataGrid.getPositionElementArea(FcPositionProperty.BOTTOM_LEFT)
    const w = targetSizeInner
    const h = targetSizeInner
    const xsize = dataGrid.getSize(w + sizePadding * 2)
    const ysize = dataGrid.getSize(h + sizePadding * 2)
    const x = area.x * xsize + dataGrid.getEmptyOffset(area.x) - sizePadding
    const y = area.y * ysize - sizePadding + dataGrid.getEmptyOffset(area.y)
    const boxW = area.width * xsize
    const boxH = area.height * ysize
    const x2 = containerSvg.width / 2
    const y2 = containerSvg.height / 2
    let start: number
    let end: number
    if (dataGrid.rotate) {
      start = getDistance(x, y, x2, y2)
      end = getDistance(x + boxW, y + boxH, x2, y2)
    } else {
      start = getDistance(x, y + boxH, x2, y2)
      end = getDistance(x + boxW, y, x2, y2)
    }
    const midPoint = start - (start - end) * (container.borderInsetEyes / 100)
    container.borderInsetCalcValue = containerSvg.width / 2 - midPoint
  }

  let radius: number | undefined
  if (container.shape === FcShape.CIRCLE) {
    radius = targetSizeInner / 2 - container.borderSize - container.borderInsetCalcValue
    if (!container.borderInsetCalcValue && container.borderOutlineSize) {
      radius -= container.borderOutlineSize
    }
  }

  let viewBox = ''
  if (radius && container.borderInsetEyes !== undefined) {
    const allIntersects: number[][] = []
    for (const element of positionElements) {
      const intersects: number[] = []
      const area = dataGrid.getPositionElementArea(element.position.property)
      const w = targetSizeInner
      const h = targetSizeInner
      const xsize = dataGrid.getSize(w + sizePadding * 2)
      const ysize = dataGrid.getSize(h + sizePadding * 2)
      const x = area.x * xsize + dataGrid.getEmptyOffset(area.x) - sizePadding
      const y = area.y * ysize - sizePadding + dataGrid.getEmptyOffset(area.y)
      const boxW = area.width * xsize
      const boxH = area.height * ysize

      const fullRad = targetSizeInner / 2 - container.borderInsetCalcValue
      viewBox = `${targetSizeInner / 2 - fullRad} ${targetSizeInner / 2 - fullRad} ${fullRad * 2} ${
        fullRad * 2
      }`
      intersects.push(
        ...getIntersection(x, y, boxW, 0, containerSvg.width / 2, containerSvg.height / 2, radius)
      )
      intersects.push(
        ...getIntersection(
          x + boxW,
          y,
          0,
          boxH,
          containerSvg.width / 2,
          containerSvg.height / 2,
          radius
        )
      )
      intersects.push(
        ...getIntersection(
          x,
          y + boxH,
          boxW,
          0,
          containerSvg.width / 2,
          containerSvg.height / 2,
          radius
        )
      )
      intersects.push(
        ...getIntersection(x, y, 0, boxH, containerSvg.width / 2, containerSvg.height / 2, radius)
      )
      intersects.sort((pt1, pt2) => pt1 - pt2)
      if (intersects.length === 2) {
        allIntersects.push(intersects)
        if (element.insetBorderPadding) {
          intersects[0] -= element.insetBorderPadding
          intersects[1] += element.insetBorderPadding
        }
      }
    }
    if (allIntersects.length > 0) {
      allIntersects.sort((set1, set2) => set1[0] - set2[0])
      container.borderIntersections = allIntersects
    }
  }

  let gridBg = ''
  if (dataGrid.dataProps.backgroundColor) {
    const xsize = dataGrid.getSize(containerSvg.width + sizePadding * 2)
    const ysize = dataGrid.getSize(containerSvg.height + sizePadding * 2)
    const x =
      dataGrid.dataModuleOffset * xsize +
      dataGrid.getEmptyOffset(dataGrid.dataModuleOffset) -
      sizePadding
    const y =
      dataGrid.dataModuleOffset * ysize +
      dataGrid.getEmptyOffset(dataGrid.dataModuleOffset) -
      sizePadding
    const w = dataGrid.dataModuleCount * xsize
    const h = dataGrid.dataModuleCount * ysize
    gridBg = `<rect x="${roundDecimal(x)}" y="${roundDecimal(y)}" width="${roundDecimal(
      w
    )}" height="${roundDecimal(h)}" ${fcBuildSvgColor(
      dataGrid.dataProps.backgroundColor,
      FcColorType.FILL,
      container.useCmyk
    )} shape-rendering="crispEdges"/>`
  }

  await fcRenderContainer(
    container,
    containerSvg,
    reserved,
    gridBg,
    container.maskImageUrl ? true : false
  )

  let maskId = ''
  let gradientMaskId = ''
  let gradientId = ''
  let maskStr = ''

  if (container.maskImageUrl) {
    maskId = fcGetDomId()
    maskStr = `<mask id=${maskId}>`
  }

  if (container.containerColorGradient) {
    const idBase = fcGetDomId()
    gradientId = idBase + '-grd'
    const gradientDef = fcGenerateGradient(
      containerSvg.getUseCmyk(),
      container.containerColorGradient,
      gradientId,
      true
    )
    containerSvg.addToDom(gradientDef)
    gradientMaskId = idBase + '-grm'
    maskStr = `<mask id="${gradientMaskId}">`
  }

  await fcRenderDataGrid(
    dataGrid,
    containerSvg,
    reserved,
    sizePadding,
    logo,
    centerText,
    radius,
    container.borderInsetCalcValue ? true : false,
    overlays,
    margin + rimIndent,
    container.overlayRadiusMask,
    container.dataCheckOnly,
    maskStr
  )

  if (gradientMaskId) {
    containerSvg.addToDom('</mask>')
    containerSvg.addToDom(
      `<rect width="${containerSvg.width}" height="${containerSvg.height}" fill="url(#${gradientId})" mask="url(#${gradientMaskId})"/>`
    )
  }

  if (!maskId) {
    containerSvg.endClip()
  }

  if (!container.dataCheckOnly) {
    for (const element of positionElements) {
      const area = dataGrid.getPositionElementArea(element.position.property)
      const w = targetSizeInner
      const h = targetSizeInner
      const xsize = dataGrid.getSize(w + sizePadding * 2)
      const ysize = dataGrid.getSize(h + sizePadding * 2)
      const x = area.x * xsize + dataGrid.getEmptyOffset(area.x) - sizePadding
      const y = area.y * ysize - sizePadding + dataGrid.getEmptyOffset(area.y)
      const boxW = area.width * xsize
      const str = await fcPositionElementRenderer(element, x, y, boxW, container.useCmyk)
      containerSvg.addToDom(str)
    }
  }

  if (maskId) {
    await fcRenderContainerText(containerSvg, fcGetDomId(), container, reserved)
    containerSvg.addToDom('</mask>')
    await fcRenderContainerMaskImage(containerSvg, maskId, container)
    containerSvg.endClip()
  }

  await fcRenderContainerOverlay(container, containerSvg, reserved)
  if (parentSvg) {
    parentSvg.addOverlay(containerSvg.getOverlay())
  } else {
    containerSvg.setOverlay()
  }

  if (rimIndent > 0) {
    const containerSvgRim = new FcSvg(margin, margin, targetSize, targetSize, container.useCmyk)
    await fcRenderContainerRim(container, containerSvgRim)
    containerSvgRim.addSvg(containerSvg)
    containerSvgRim.endClip()
    if (parentSvg) {
      parentSvg.addSvg(containerSvgRim)
      parentSvg.erasedData = dataGrid.getErasedData()
      copyContainerData(parentSvg, containerSvg)
      return parentSvg
    } else {
      containerSvg.erasedData = dataGrid.getErasedData()
      return containerSvgRim
    }
  }

  if (parentSvg) {
    if (inset) {
      containerSvg.setViewBox(viewBox)
      parentSvg.addSvg(containerSvg)
    } else {
      parentSvg.addSvg(containerSvg)
      parentSvg.setViewBox(viewBox)
    }
    parentSvg.setOverlay()
    parentSvg.erasedData = dataGrid.getErasedData()
    copyContainerData(parentSvg, containerSvg)
    return parentSvg
  }
  containerSvg.setViewBox(viewBox)
  containerSvg.erasedData = dataGrid.getErasedData()
  return containerSvg
}

function copyContainerData(parentSvg: FcSvg, containerSvg: FcSvg): void {
  parentSvg.centerTextClipped = containerSvg.centerTextClipped
  parentSvg.centerTextWidthMax = containerSvg.centerTextWidthMax
  parentSvg.logoNewHeight = containerSvg.logoNewHeight
  parentSvg.logoNewWidth = containerSvg.logoNewWidth
}
